import { Eye, FileCheck, RefreshCw, Trash2, Upload } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { InspectArtifactModal } from '@/components/artifact-inspector/InspectArtifactModal';
import { Button } from '@/components/ui/button';
import { useGetBlobDetailQuery } from '@/services/api/blobService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';

import { PhotoIdUploadModal } from './PhotoIdUploadModal';

interface ClientPhotoIdFieldProps {
  clientId: string;
  matterId: string;
  isEditing: boolean;
}

export const ClientPhotoIdField: React.FC<ClientPhotoIdFieldProps> = ({
  clientId,
  matterId,
  isEditing,
}) => {
  const [showUploader, setShowUploader] = useState(false);
  const [showInspector, setShowInspector] = useState(false);

  // Fetch existing HIPAA authorization for this matter
  const { data: existingAuth, refetch } =
    treatmentRecordRequestService.useMattersHipaaAuthorizationForMatterRetrieveQuery(
      { matterId },
    );

  // Mutations for creating/updating HIPAA authorization
  const [updateAuth, { isLoading: isSaving }] =
    treatmentRecordRequestService.useMattersHipaaAuthorizationUpdateMutation();

  // Get blob details for the selected photo ID
  const { data: photoIdBlob, isLoading } = useGetBlobDetailQuery(
    { matterId, blobId: existingAuth?.client_physical_id || '' },
    { skip: !existingAuth?.client_physical_id },
  );

  const hasPhotoId = Boolean(existingAuth?.client_physical_id && photoIdBlob);

  const handleSelect = async (blobId: string) => {
    try {
      if (existingAuth?.id) {
        await updateAuth({
          matterId,
          authorizationId: existingAuth.id,
          updateTreatmentRecordRequestHipaaAuthorizationSchema: {
            client_physical_id: blobId,
            authorization_template_id: existingAuth.authorization_template_id,
          },
        }).unwrap();

        toast.success('Photo ID updated successfully');
        setShowUploader(false);
        refetch();
      }
    } catch (err) {
      toast.error('Failed to update photo ID');
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm('Remove photo ID from file?');
    if (!confirmed) return;

    try {
      if (existingAuth?.id) {
        await updateAuth({
          matterId,
          authorizationId: existingAuth.id,
          updateTreatmentRecordRequestHipaaAuthorizationSchema: {
            client_physical_id: null,
            authorization_template_id: existingAuth.authorization_template_id,
          },
        }).unwrap();

        toast.success('Photo ID removed');
        refetch();
      }
    } catch (err) {
      toast.error('Failed to remove photo ID');
    }
  };

  // Format date with better labeling
  const formatUploadDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (diffInDays === 0) return 'Uploaded today';
    if (diffInDays === 1) return 'Uploaded yesterday';
    if (diffInDays < 7) return `Uploaded ${diffInDays} days ago`;
    if (diffInDays < 30)
      return `Uploaded ${Math.floor(diffInDays / 7)} weeks ago`;
    if (diffInDays < 365)
      return `Uploaded ${Math.floor(diffInDays / 30)} months ago`;

    return `Uploaded ${date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}`;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-3 border-t border-gray-100">
        <dt className="text-xs font-medium text-gray-900">Photo ID</dt>
        <dd className="mt-0.5 text-xs sm:col-span-2 sm:mt-0">
          <div className="animate-pulse h-4 w-24 bg-gray-200 rounded" />
        </dd>
      </div>
    );
  }

  const isImage = photoIdBlob?.file_mime_type?.startsWith('image/');
  const displayUrl = isImage ? photoIdBlob?.download_url : null;

  return (
    <>
      <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-3 border-t-2 border-gray-200 bg-gray-50/50">
        <dt className="text-xs font-semibold text-gray-900 flex items-center gap-1">
          <FileCheck className="h-3.5 w-3.5 text-gray-600" />
          Photo ID
        </dt>
        <dd className="mt-1 sm:col-span-2 sm:mt-0">
          {hasPhotoId ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* Interactive thumbnail */}
                <button
                  type="button"
                  onClick={() => setShowInspector(true)}
                  className="block flex-shrink-0 group/photoid relative"
                >
                  {displayUrl ? (
                    <img
                      src={displayUrl}
                      alt="Client ID"
                      className="h-12 w-16 object-cover rounded border-2 border-green-200 group-hover/photoid:border-green-400 transition-colors"
                    />
                  ) : (
                    <div className="h-12 w-16 rounded border-2 border-green-200 group-hover/photoid:border-green-400 transition-colors bg-gray-100 flex items-center justify-center">
                      <FileCheck className="h-6 w-6 text-gray-400" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover/photoid:bg-black/10 transition-colors rounded flex items-center justify-center">
                    <Eye className="h-4 w-4 text-white opacity-0 group-hover/photoid:opacity-100 transition-opacity" />
                  </div>
                </button>

                <div className="flex items-center">
                  <span className="text-xs font-medium text-green-700">
                    On file
                  </span>
                </div>
              </div>

              {/* Actions for existing photo ID */}
              <div className="flex items-center gap-1">
                <Button
                  onClick={() => setShowUploader(true)}
                  variant="outline"
                  size="sm"
                  className="h-7 px-2 gap-1 text-xs"
                  disabled={isSaving}
                >
                  <RefreshCw className="h-3 w-3" />
                  Replace
                </Button>
                {isEditing && (
                  <Button
                    onClick={handleDelete}
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 gap-1 text-xs text-red-600 hover:text-red-700"
                    disabled={isSaving}
                  >
                    <Trash2 className="h-3 w-3" />
                    Remove
                  </Button>
                )}
              </div>
            </div>
          ) : (
            // Prominent upload button when no photo ID
            <Button
              onClick={() => setShowUploader(true)}
              variant="outline"
              size="sm"
              className="h-8 px-3 gap-1.5 text-xs bg-white hover:bg-gray-50 border-gray-300"
            >
              <Upload className="h-3.5 w-3.5" />
              Upload Photo ID
            </Button>
          )}
        </dd>
      </div>

      {/* Upload Modal */}
      <PhotoIdUploadModal
        isOpen={showUploader}
        onClose={() => setShowUploader(false)}
        onSelect={handleSelect}
        onClear={handleDelete}
        matterId={matterId}
        isUploading={isSaving}
        existingBlobId={existingAuth?.client_physical_id}
      />

      {/* Artifact Inspector Modal */}
      {photoIdBlob?.artifact_digest?.artifact_id && (
        <InspectArtifactModal
          artifactId={photoIdBlob?.artifact_digest?.artifact_id}
          isOpen={showInspector}
          onOpenChange={setShowInspector}
        />
      )}
    </>
  );
};
